import { InputContent } from './components/styles/Input.js';
import './components/styles/Time.js';
import { SelectContainer, DropdownIconContainer } from './components/styles/Select.js';
import './components/styles/Field.js';
import './components/styles/Password.js';
import { ThemingContext } from '@doordash/design-language';
import WrappedFieldWrapper from './components/FieldWrapper.js';
import ChevronDownIcon from './components/ChevronDownIcon.js';
import React__default from 'react';
import { getThemeFieldTextStyles } from './utils.js';
import groupBy from 'lodash.groupby';
import { getPropsToForward } from '@doordash/utilities';
import { SelectSize } from './constants.js';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr && (typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]); if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
// don't allow arrays to be returned in render methods
// for use in JSX.

var renderOptions = function renderOptions(_ref) {
  var options = _ref.options;
  return options.map(function (option) {
    return /*#__PURE__*/React__default.createElement("option", {
      key: option.value,
      value: option.value
    }, option.name);
  });
}; // See note above about why this is a function and not
// a component used in JSX.


var groupOptions = function groupOptions(_ref2) {
  var options = _ref2.options;
  var groupedOptions = groupBy(options, 'group');
  var groups = Object.keys(groupedOptions); // If there are multiple groups, render optgroups

  if (groups.length > 1) {
    return Object.entries(groupedOptions).map(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
          group = _ref4[0],
          groupsOptions = _ref4[1];

      // Group all named groups
      if (group !== 'undefined') {
        return /*#__PURE__*/React__default.createElement("optgroup", {
          label: group,
          key: group
        }, renderOptions({
          options: groupsOptions
        }));
      } // If group isn't set on the option ('undefined'),
      // render the options directly


      return renderOptions({
        options: groupsOptions
      });
    });
  }

  return renderOptions({
    options: options
  });
};
/**
 * https://developers.google.com/web/fundamentals/accessibility/semantics-aria/aria-labels-and-relationships
 * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html
 * https://developers.google.com/web/fundamentals/design-and-ui/input/forms/
 */


var SelectContentInner = function SelectContentInner(_ref5, ref) {
  var id = _ref5.id,
      label = _ref5.label,
      value = _ref5.value,
      _onChange = _ref5.onChange,
      isRequired = _ref5.isRequired,
      isDisabled = _ref5.isDisabled,
      isLabelHidden = _ref5.isLabelHidden,
      autoFocus = _ref5.autoFocus,
      onFocus = _ref5.onFocus,
      onBlur = _ref5.onBlur,
      placeholder = _ref5.placeholder,
      name = _ref5.name,
      disabledValue = _ref5.disabledValue,
      options = _ref5.options,
      size = _ref5.size,
      propsToForward = _objectWithoutProperties(_ref5, ["id", "label", "value", "onChange", "isRequired", "isDisabled", "isLabelHidden", "autoFocus", "onFocus", "onBlur", "placeholder", "name", "disabledValue", "options", "size"]);

  var theme = React__default.useContext(ThemingContext);
  var inputTextStyles = getThemeFieldTextStyles({
    theme: theme,
    size: size,
    styleType: 'Input'
  });
  return /*#__PURE__*/React__default.createElement(SelectContainer, null, /*#__PURE__*/React__default.createElement(InputContent, _extends({
    id: id,
    ref: ref,
    as: "select",
    value: value,
    onChange: function onChange(event) {
      return _onChange(event.target.value, event);
    } // Only one of `<label>` and `aria-label` should be implemented
    // Using two or more together (e.g., a hidden <label> and a
    // duplicate title attribute) can cause information to be
    // repeated by a screen reader.
    // http://webaim.org/techniques/forms/advanced#hiddenlabel
    ,
    "aria-label": isLabelHidden ? label : null,
    required: isRequired,
    disabled: isDisabled,
    autoFocus: autoFocus,
    onFocus: onFocus,
    onBlur: onBlur,
    placeholder: placeholder,
    name: name,
    inputSize: size,
    mobileInputTextStyles: inputTextStyles.mobile,
    desktopInputTextStyles: inputTextStyles.desktop
  }, propsToForward), disabledValue && /*#__PURE__*/React__default.createElement("option", {
    disabled: true,
    value: ""
  }, typeof disabledValue === 'string' ? disabledValue : ''), groupOptions({
    options: options
  })), /*#__PURE__*/React__default.createElement(DropdownIconContainer, null, /*#__PURE__*/React__default.createElement(ChevronDownIcon, {
    color: isDisabled ? theme.Colors.FieldsForegroundDisabled : theme.Colors.FieldsForegroundInactive
  })));
}; // forwardRef can't infer generic types so it needs some help. (see https://stackoverflow.com/a/58473012 for explanation)
// there are a handful of work arounds, in this case it seemed like type assertion was the best way to go.
// see: https://fettblog.eu/typescript-react-generic-forward-refs/#option-1%3A-type-assertion


var SelectContent = /*#__PURE__*/React__default.forwardRef(SelectContentInner);

var Select = function Select(props) {
  var _props$size = props.size,
      size = _props$size === void 0 ? SelectSize.Medium : _props$size,
      autoFocus = props.autoFocus,
      isDisabled = props.isDisabled,
      onFocus = props.onFocus,
      onBlur = props.onBlur,
      onChange = props.onChange,
      onClick = props.onClick,
      value = props.value,
      label = props.label,
      id = props.id,
      isLabelHidden = props.isLabelHidden,
      isRequired = props.isRequired,
      isRounded = props.isRounded,
      error = props.error,
      description = props.description,
      hint = props.hint,
      placeholder = props.placeholder,
      renderBeforeContent = props.renderBeforeContent,
      renderAfterContent = props.renderAfterContent,
      renderConnectedBefore = props.renderConnectedBefore,
      renderConnectedAfter = props.renderConnectedAfter,
      name = props.name,
      options = props.options,
      disabledValue = props.disabledValue;
  var propsToForward = getPropsToForward({
    props: props,
    prefixesToForward: ['data-', 'aria-']
  });
  return /*#__PURE__*/React__default.createElement(WrappedFieldWrapper, {
    label: label,
    description: description,
    hint: hint,
    error: error,
    isDisabled: isDisabled,
    isLabelHidden: isLabelHidden,
    isRounded: isRounded,
    renderBeforeContent: renderBeforeContent,
    renderAfterContent: renderAfterContent,
    renderConnectedBefore: renderConnectedBefore,
    renderConnectedAfter: renderConnectedAfter,
    size: size,
    onClick: onClick,
    id: id
  }, /*#__PURE__*/React__default.createElement(SelectContent, _extends({
    label: label,
    autoFocus: autoFocus,
    onBlur: onBlur,
    onFocus: onFocus,
    onChange: onChange,
    value: value,
    name: name,
    isDisabled: isDisabled,
    isLabelHidden: isLabelHidden,
    isRequired: isRequired,
    placeholder: placeholder,
    options: options,
    disabledValue: disabledValue,
    size: size
  }, propsToForward)));
};

Select.Sizes = SelectSize;

export { Select as default };
